import React, { useState, useEffect } from 'react';
import authedFetch from '../../lib/authedFetch';
import { Users, GraduationCap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import secureStorage from '../../lib/secureStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const OrgSegmentsPanel = ({ orgId, orgName }) => {
  const [cohorts, setCohorts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSegments = async () => {
      setLoading(true);
      const token = secureStorage.getItem('session_token');
      try {
        const [cohortsRes, programsRes] = await Promise.all([
          authedFetch(`${API_URL}/api/segments/cohorts?include_demo=false&organization_id=${orgId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
          authedFetch(`${API_URL}/api/segments/programs?include_demo=false&organization_id=${orgId}`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        if (cohortsRes.ok) { const d = await cohortsRes.json(); setCohorts(d.cohorts || []); }
        if (programsRes.ok) { const d = await programsRes.json(); setPrograms(d.programs || []); }
      } catch (e) { /* segment fetch error */ }
      setLoading(false);
    };
    if (orgId) fetchSegments();
  }, [orgId]);

  if (loading) {
    return <div className="text-center py-8 text-gray-400 text-sm">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="org-segments-panel">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users size={16} className="text-blue-500" />
            Cohorts
            <Badge variant="outline" className="ml-1 text-xs">{cohorts.length}</Badge>
          </CardTitle>
          <CardDescription>Target populations for {orgName}</CardDescription>
        </CardHeader>
        <CardContent>
          {cohorts.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No cohorts defined for this organization.</p>
          ) : (
            <div className="divide-y">
              {cohorts.map(c => (
                <div key={c.id} className="flex items-center justify-between py-3" data-testid={`org-cohort-${c.id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{c.name}</p>
                    <p className="text-xs text-slate-500">{c.description || 'No description'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs capitalize">{c.type || 'ongoing'}</Badge>
                    <span className="text-xs text-gray-400">{c.member_count || 0} data points</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <GraduationCap size={16} className="text-emerald-500" />
            Programs
            <Badge variant="outline" className="ml-1 text-xs">{programs.length}</Badge>
          </CardTitle>
          <CardDescription>Learning programs for {orgName}</CardDescription>
        </CardHeader>
        <CardContent>
          {programs.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No programs defined for this organization.</p>
          ) : (
            <div className="divide-y">
              {programs.map(p => (
                <div key={p.id} className="flex items-center justify-between py-3" data-testid={`org-program-${p.id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{p.name}</p>
                    <p className="text-xs text-slate-500">{p.description || 'No description'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-xs ${p.status === 'active' ? 'bg-emerald-100 text-emerald-700' : p.status === 'completed' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                      {p.status || 'draft'}
                    </Badge>
                    <span className="text-xs text-gray-400">{p.data_point_count || 0} data points</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
